import React, { useState } from 'react'
import { ArrowLeft, Menu } from 'lucide-react'

import CurrencyLogo from '@/components/CurrencyLogo'
import { useTranslation } from '@/features/locale/LocaleProvider'
import { useMobileShellMenuOptional } from '@/mobile/platform/MobileShellMenuContext'
import {
  isContractDiscoveryActive,
  TokenPickerAddressSearchBlock,
  TokenPickerListRow,
  TokenPickerNoResultsView,
  TokenPickerSearchField,
} from '@/components/token-picker'
import { useAppWeb3 } from '@/hooks'
import type { ChainId, Currency } from '@/sdk'

import {
  currencyKey,
  useMobileTokenPickerGroups,
  type MobileTokenPickerMode,
} from '../token-picker'

export function MobileTokenPickerContent({
  chainId,
  mode,
  title,
  onSelect,
  onBack,
  account: accountProp,
}: {
  chainId: ChainId
  mode: MobileTokenPickerMode
  title: string
  onSelect: (currency: Currency) => void
  onBack: () => void
  account?: string | null
}) {
  const t = useTranslation()
  const picker = t.shell.tokenPicker
  const { account: connectedAccount } = useAppWeb3()
  const account = accountProp ?? connectedAccount
  const shellMenu = useMobileShellMenuOptional()
  const [search, setSearch] = useState('')
  const groups = useMobileTokenPickerGroups(chainId, search, account, mode)

  const showSearchEmpty =
    groups.isSearching &&
    !groups.isResolvingAddress &&
    !groups.importedByAddress &&
    !groups.addressNotFound &&
    groups.tokens.length === 0
  const contractDiscovery = isContractDiscoveryActive(groups.importedByAddress, groups.searchToken)

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      <header className="flex shrink-0 items-center gap-2 border-b border-white/10 px-3 py-3">
        <button type="button" className="mobile-app__menu-btn" onClick={onBack} aria-label={t.common.back}>
          <ArrowLeft className="size-5" />
        </button>
        <h1 className="min-w-0 flex-1 text-center text-base font-semibold">{title}</h1>
        {shellMenu ? (
          <button
            type="button"
            className="mobile-app__menu-btn"
            onClick={shellMenu.openMenu}
            aria-label={t.common.openMenu}
          >
            <Menu className="size-5" />
          </button>
        ) : (
          <span className="size-10" aria-hidden />
        )}
      </header>

      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3"
        data-mobile-scroll-container
      >
        <TokenPickerSearchField
          value={search}
          onChange={setSearch}
          variant="nutrimatic"
          className="mb-3 px-0"
        />

        <TokenPickerAddressSearchBlock
          isAddressSearch={groups.isAddressSearch}
          isResolvingAddress={groups.isResolvingAddress}
          importedByAddress={groups.importedByAddress}
          addressNotFound={groups.addressNotFound}
          searchToken={groups.searchToken}
          onImport={() => setSearch('')}
          onCancel={() => setSearch('')}
          variant="nutrimatic"
          className="mb-3"
        />

        {contractDiscovery ? null : groups.isSearching ? (
          <TokenSection title={t.common.results}>
            {groups.tokens.map((currency) => (
              <TokenPickerListRow
                key={currencyKey(currency)}
                currency={currency}
                balance={groups.balanceByKey.get(currencyKey(currency))}
                onSelect={onSelect}
              />
            ))}
            {showSearchEmpty ? (
              <TokenPickerNoResultsView variant="nutrimatic" onCancel={() => setSearch('')} />
            ) : null}
          </TokenSection>
        ) : mode === 'pay' ? (
          <>
            <TokenSection title={picker.withBalance}>
              {groups.payGroups.withBalance.length > 0 ? (
                groups.payGroups.withBalance.map(({ currency, balance }) => (
                  <TokenPickerListRow
                    key={currencyKey(currency)}
                    currency={currency}
                    balance={balance}
                    onSelect={onSelect}
                  />
                ))
              ) : (
                <p className="mobile-app__empty">{picker.noVisibleBalance}</p>
              )}
            </TokenSection>
            <TokenSection title={picker.withoutBalance}>
              {groups.payGroups.withoutBalance.map((currency) => (
                <TokenPickerListRow key={currencyKey(currency)} currency={currency} onSelect={onSelect} />
              ))}
            </TokenSection>
          </>
        ) : mode === 'receive' ? (
          <>
            {groups.receiveGroups.featured.length > 0 ? (
              <TokenSection title={picker.featured}>
                <div className="grid grid-cols-3 gap-2">
                  {groups.receiveGroups.featured.map((currency) => (
                    <button
                      key={currencyKey(currency)}
                      type="button"
                      className="mobile-app__card flex flex-col items-start gap-1 p-2 text-left"
                      onClick={() => onSelect(currency)}
                    >
                      <CurrencyLogo currency={currency} size="28px" />
                      <span className="text-sm font-bold">{currency.symbol}</span>
                    </button>
                  ))}
                </div>
              </TokenSection>
            ) : null}
            <TokenSection title={picker.all}>
              {groups.receiveGroups.all.map((currency) => (
                <TokenPickerListRow
                  key={currencyKey(currency)}
                  currency={currency}
                  balance={groups.balanceByKey.get(currencyKey(currency))}
                  onSelect={onSelect}
                />
              ))}
            </TokenSection>
          </>
        ) : (
          <TokenSection title={picker.tokens}>
            {groups.receiveGroups.all.map((currency) => (
              <TokenPickerListRow
                key={currencyKey(currency)}
                currency={currency}
                balance={groups.balanceByKey.get(currencyKey(currency))}
                onSelect={onSelect}
              />
            ))}
          </TokenSection>
        )}
      </div>
    </div>
  )
}

function TokenSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4">
      <h2 className="mb-2 text-sm font-bold">{title}</h2>
      <div className="flex flex-col gap-1.5">{children}</div>
    </section>
  )
}
